import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { toPercent } from '@client/toolkit';

import { OnboardingBuilding, BuildingRole } from '../../onboarding/onboarding.types';
import { OnboardingActions } from '../../onboarding/onboarding-actions';

const PLACEHOLDER = '\u2013';

interface StepRollerProps {
  buildings: OnboardingBuilding[];
  onBuildingsChange: (buildings: OnboardingBuilding[]) => void;
  onBack: () => void;
  onNext: () => void;
}

type ActiveRole = 'Eier' | 'Investor' | 'Forvalter';

const ROLE_COLORS: Record<ActiveRole, string> = {
  Eier: 'border-l-emerald-400',
  Investor: 'border-l-cyan-400',
  Forvalter: 'border-l-amber-400',
};

const ROLE_SELECT_COLORS: Record<ActiveRole, string> = {
  Eier: 'bg-emerald-500/20 text-emerald-400',
  Investor: 'bg-cyan-500/20 text-cyan-400',
  Forvalter: 'bg-amber-500/20 text-amber-400',
};

const BUILDING_TYPE_LABELS: Record<string, string> = {
  Office: 'Kontor',
  RetailHighStreet: 'Handel High Street',
  Retail: 'Handel',
  Logistics: 'Logistikk',
  Industrial: 'Industri',
  Hotel: 'Hotell',
  Healthcare: 'Helse',
  Education: 'Undervisning',
  Mixed: 'Kombinasjon',
};

export function StepRoller({ buildings, onBuildingsChange, onBack, onNext }: StepRollerProps) {
  const { t } = useTranslation('portfolio');
  const selectedBuildings = useMemo(() => buildings.filter((b) => b.selected), [buildings]);

  const roleCounts = useMemo(() => {
    const counts = { Eier: 0, Investor: 0, Forvalter: 0 };
    selectedBuildings.forEach((b) => {
      if (b.role && b.role in counts) counts[b.role as ActiveRole]++;
    });
    return counts;
  }, [selectedBuildings]);

  const totalBra = useMemo(
    () => selectedBuildings.reduce((sum, b) => sum + b.bra, 0),
    [selectedBuildings],
  );

  const handleRoleChange = (id: string, role: BuildingRole) => {
    const updated = buildings.map((b) => (b.id === id ? { ...b, role } : b));
    onBuildingsChange(updated);
  };

  const handleAssignAll = (role: ActiveRole) => {
    const updated = buildings.map((b) => (b.selected ? { ...b, role } : b));
    onBuildingsChange(updated);
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-white mb-2">Roller per bygg</h2>
      <p className="text-white/60 mb-8">
        Automatisk detektert basert på hjemmelshaver og konsernstruktur. Endre roller nedenfor.
      </p>

      {/* Summary cards */}
      <div className="grid grid-cols-4 gap-3 mb-8">
        <div className="p-4 rounded-lg border border-emerald-400/30 bg-surface-base">
          <div className="text-3xl font-bold text-white">{roleCounts.Eier}</div>
          <div className="text-xs text-white/50 uppercase tracking-wider mt-1">Bygg som eier</div>
        </div>
        <div className="p-4 rounded-lg border border-cyan-400/30 bg-surface-base">
          <div className="text-3xl font-bold text-white">{roleCounts.Investor}</div>
          <div className="text-xs text-white/50 uppercase tracking-wider mt-1">Bygg som investor</div>
        </div>
        <div className="p-4 rounded-lg border border-amber-400/30 bg-surface-base">
          <div className="text-3xl font-bold text-white">{roleCounts.Forvalter}</div>
          <div className="text-xs text-white/50 uppercase tracking-wider mt-1">Bygg som forvalter</div>
        </div>
        <div className="p-4 rounded-lg border border-border bg-surface-base">
          <div className="text-3xl font-bold text-white">{totalBra.toLocaleString('nb-NO')}</div>
          <div className="text-xs text-white/50 uppercase tracking-wider mt-1">M² totalt</div>
        </div>
      </div>

      {/* Bulk assign */}
      {selectedBuildings.length > 1 && (
        <div className="flex items-center gap-2 mb-3 text-sm">
          <span className="text-white/50">Tildel alle byggene rollen:</span>
          <button
            type="button"
            onClick={() => handleAssignAll('Eier')}
            className="px-3 py-1 rounded border border-emerald-400/40 text-emerald-400 hover:bg-emerald-500/10 transition-colors"
          >
            Eier
          </button>
          <button
            type="button"
            onClick={() => handleAssignAll('Investor')}
            className="px-3 py-1 rounded border border-cyan-400/40 text-cyan-400 hover:bg-cyan-500/10 transition-colors"
          >
            Investor
          </button>
          <button
            type="button"
            onClick={() => handleAssignAll('Forvalter')}
            className="px-3 py-1 rounded border border-amber-400/40 text-amber-400 hover:bg-amber-500/10 transition-colors"
          >
            Forvalter
          </button>
          <span className="text-white/30 ml-2 text-xs">
            Overstyrer eksisterende valg — du kan justere per bygg nedenfor.
          </span>
        </div>
      )}

      {/* Table */}
      <div className="border border-border rounded-lg overflow-hidden">
        <div className="grid grid-cols-[1fr_100px_100px_80px_140px] gap-4 px-5 py-3 text-xs text-white/40 uppercase tracking-wider bg-surface-base border-b border-border">
          <span>Adresse</span>
          <span>Type</span>
          <span>BRA</span>
          <span>Energi</span>
          <span>Velg rolle</span>
        </div>

        {selectedBuildings.map((building) => (
          <div
            key={building.id}
            className={`grid grid-cols-[1fr_100px_100px_80px_140px] gap-4 px-5 py-4 border-b border-border last:border-b-0 border-l-4 ${building.role ? ROLE_COLORS[building.role as ActiveRole] : 'border-l-white/20'}`}
          >
            <div>
              <div className="text-white font-medium">{building.address}</div>
              <div className="text-white/50 text-sm">
                {building.postalCode} {building.city}
                {building.countryThreeLetterCode && building.countryThreeLetterCode !== 'NOR' && (
                  <> ({building.countryThreeLetterCode})</>
                )}
              </div>
              {building.owners && building.owners.length > 0 ? (
                <div className="mt-1 space-y-0.5">
                  <div className="text-white/30 text-[10px] uppercase tracking-wider">
                    {t('onboarding.owners.heading')}
                  </div>
                  {building.owners.map((owner, index) => (
                    <div key={index} className="text-white/50 text-xs flex items-center gap-1.5">
                      <span>{owner.name ?? PLACEHOLDER}</span>
                      {!owner.isJuridical && (
                        <span className="text-white/30">({t('onboarding.owners.physical')})</span>
                      )}
                      {owner.shareNumerator != null && owner.shareDenominator ? (
                        <span className="text-white/40">
                          {toPercent(owner.shareNumerator / owner.shareDenominator)}
                        </span>
                      ) : null}
                    </div>
                  ))}
                </div>
              ) : building.ownerName ? (
                <div className="text-white/40 text-xs mt-0.5">
                  {t('onboarding.owners.label')}: {building.ownerName}
                </div>
              ) : null}
            </div>
            <div className="flex items-center">
              <span className="text-xs px-2 py-1 rounded bg-surface-elevated text-white/70">
                {BUILDING_TYPE_LABELS[building.type] ?? building.type}
              </span>
            </div>
            <div className="flex items-center text-white">
              {building.bra.toLocaleString('nb-NO')}
            </div>
            <div className="flex items-center">
              {building.energyLabel ? (
                <span className="text-xs px-2 py-1 rounded bg-surface-elevated text-white/70 font-medium">
                  {building.energyLabel}
                </span>
              ) : (
                <span className="text-white/30">-</span>
              )}
            </div>
            <div className="flex items-center">
              <select
                value={building.role}
                onChange={(e) => handleRoleChange(building.id, e.target.value as BuildingRole)}
                className={`text-sm rounded pl-3 pr-8 py-1.5 border-0 focus:outline-none focus:ring-1 focus:ring-brand-primary cursor-pointer appearance-none bg-[url('data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2020%2020%22%20fill%3D%22white%22%3E%3Cpath%20fill-rule%3D%22evenodd%22%20d%3D%22M5.293%207.293a1%201%200%20011.414%200L10%2010.586l3.293-3.293a1%201%200%20111.414%201.414l-4%204a1%201%200%2001-1.414%200l-4-4a1%201%200%20010-1.414z%22%20clip-rule%3D%22evenodd%22%2F%3E%3C%2Fsvg%3E')] bg-[length:1.25rem] bg-[right_0.5rem_center] bg-no-repeat ${building.role ? ROLE_SELECT_COLORS[building.role as ActiveRole] : 'bg-surface-elevated text-white/50'}`}
              >
                <option value="" disabled>-Velg-</option>
                <option value="Eier">Eier</option>
                <option value="Investor">Investor</option>
                <option value="Forvalter">Forvalter</option>
              </select>
            </div>
          </div>
        ))}
      </div>

      <OnboardingActions onBack={onBack} onNext={onNext} nextDisabled={selectedBuildings.some((b) => !b.role)} />
    </div>
  );
}
